import { AI_AWARENESS_PASSING_ANSWER_COUNT } from '../../shared/trainingRequirements';

export const AI_AWARENESS_TRAINING_STATUSES = [
  'Introduction',
  'AI at Work',
  'Data Privacy',
  'Misinformation',
  'Security & Access',
  'Accountability',
  'The Upside',
  'Key Takeaways',
  'Knowledge Check',
  'Question 1',
  'Question 2',
  'Question 3',
  'Question 4',
  'Question 5',
  'Question 6',
  'Bonus',
  'Attestation',
  'Completed',
] as const;

export type AiAwarenessTrainingStatus = (typeof AI_AWARENESS_TRAINING_STATUSES)[number];
export type AiAwarenessAnswerOption = 'A' | 'B' | 'C' | 'D' | 'TRUE' | 'FALSE';

export const AI_AWARENESS_TRAINING_SLIDES = [
  { status: 'Introduction' as const, title: 'AI Awareness' },
  { status: 'AI at Work' as const, title: 'AI Is Already Part of the Work' },
  { status: 'Data Privacy' as const, title: 'What Not to Paste Into a Chatbot' },
  { status: 'Misinformation' as const, title: "AI Sounds Sure Even When It's Wrong" },
  { status: 'Security & Access' as const, title: 'Accounts, Logins, and Where Files Land' },
  { status: 'Accountability' as const, title: 'Your Name Is Still On It' },
  { status: 'The Upside' as const, title: 'Where This Actually Pays Off' },
  { status: 'Key Takeaways' as const, title: "Nice Work. Here's the Short Version." },
] as const;

export const AI_AWARENESS_TRAINING_QUESTIONS = [
  {
    status: 'Question 1' as const,
    fieldName: 'question1Answer' as const,
    prompt: 'Which of these could point to a specific client?',
    options: [
      { value: 'A' as const, label: 'Client initials and date of birth' },
      { value: 'B' as const, label: 'How many clients we served in July' },
      { value: 'C' as const, label: 'A blank copy of our intake form' },
      { value: 'D' as const, label: 'The name of a common medication' },
    ],
  },
  {
    status: 'Question 2' as const,
    fieldName: 'question2Answer' as const,
    prompt: 'Why is a personal AI account riskier than a work one?',
    options: [
      { value: 'A' as const, label: 'It often gives less accurate answers' },
      { value: 'B' as const, label: 'The company cannot control the tool' },
      { value: 'C' as const, label: 'It may have lower usage limits' },
      { value: 'D' as const, label: "IT can't reset your password for you" },
    ],
  },
  {
    status: 'Question 3' as const,
    fieldName: 'question3Answer' as const,
    prompt: 'AI gives an answer and names a source. What next?',
    options: [
      { value: 'A' as const, label: 'Trust it, since it named a source' },
      { value: 'B' as const, label: 'Ask the AI if it is sure' },
      { value: 'C' as const, label: 'Check the source yourself' },
      { value: 'D' as const, label: 'Send it, but say AI wrote it' },
    ],
  },
  {
    status: 'Question 4' as const,
    fieldName: 'question4Answer' as const,
    prompt: "The approved tool is down and you're in a rush. What now?",
    options: [
      { value: 'A' as const, label: 'Use your personal account this one time' },
      { value: 'B' as const, label: 'Remove the names and use a public tool' },
      { value: 'C' as const, label: 'Wait for the tool, or work without AI' },
      { value: 'D' as const, label: 'Ask a coworker to use their account' },
    ],
  },
  {
    status: 'Question 5' as const,
    fieldName: 'question5Answer' as const,
    prompt: 'AI writes a letter and you send it. Who is responsible?',
    options: [
      { value: 'A' as const, label: 'The company that makes the AI tool' },
      { value: 'B' as const, label: 'You, just like anything else you send' },
      { value: 'C' as const, label: 'Your manager, who approved the tool' },
      { value: 'D' as const, label: 'You and the AI tool share the blame' },
    ],
  },
  {
    status: 'Question 6' as const,
    fieldName: 'question6Answer' as const,
    prompt: 'Why do we limit which AI tools you can use?',
    options: [
      { value: 'A' as const, label: 'To save money on software' },
      { value: 'B' as const, label: 'To keep protected information safe' },
      { value: 'C' as const, label: 'To limit how often staff use AI' },
      { value: 'D' as const, label: 'To meet a state training rule' },
    ],
  },
  {
    status: 'Bonus' as const,
    fieldName: 'bonusAnswer' as const,
    prompt: 'Taking the names out of a document makes it safe to paste into a public AI tool.',
    options: [
      { value: 'TRUE' as const, label: 'True' },
      { value: 'FALSE' as const, label: 'False' },
    ],
  },
] as const;

export const AI_AWARENESS_TRAINING_COPY = {
  knowledgeCheck: {
    title: 'Knowledge Check',
    body: `Answer six questions and one bonus true/false question. At least ${AI_AWARENESS_PASSING_ANSWER_COUNT} of the six scored questions must be correct to pass. The bonus does not count toward your score.`,
    note: 'Submit each answer to see the guidance. If you do not pass, retry the knowledge check before continuing to the acknowledgment form.',
  },
  attestation: {
    title: 'Responsible AI Use Acknowledgment',
    intro: 'Before finishing, confirm that you understand and agree to the following:',
    bullets: [
      'I will use company-approved AI tools and company accounts for work.',
      'I will not enter client, employee, medical, or financial information into an AI tool unless it is approved for that use.',
      'I will read and verify AI-generated work before it is shared or used.',
      'I will ask HR or IT when I am unsure whether an AI use is appropriate.',
    ],
    checkboxLabel: 'I acknowledge that I completed this AI awareness training and understand my responsibilities when using AI tools.',
  },
  completed: {
    title: 'Training Complete',
    body: 'Thank you for completing the AI awareness training.',
  },
} as const;
